import React, { useState, useEffect, useCallback } from 'react';

const ALL_ROLES = ['admin', 'editor', 'creator', 'viewer'];

function getToken() {
  return sessionStorage.getItem('wp_token');
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleModal, setRoleModal] = useState(null); // { userId, name, currentRoles }
  const [savingRoles, setSavingRoles] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/publishing/admin/users', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed to load users (${res.status})`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleStatus = async (user) => {
    const next = user.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`/api/publishing/admin/users/${user.id}/status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      await fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const openRoleModal = (user) => {
    setRoleModal({
      userId: user.id,
      name: user.display_name || user.email,
      currentRoles: Array.isArray(user.roles) ? [...user.roles] : [],
    });
  };

  const handleRoleToggle = (role) => {
    setRoleModal(prev => {
      const has = prev.currentRoles.includes(role);
      return {
        ...prev,
        currentRoles: has
          ? prev.currentRoles.filter(r => r !== role)
          : [...prev.currentRoles, role],
      };
    });
  };

  const handleSaveRoles = async () => {
    if (!roleModal) return;
    setSavingRoles(true);
    try {
      const res = await fetch(`/api/publishing/admin/users/${roleModal.userId}/roles`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ roles: roleModal.currentRoles }),
      });
      if (!res.ok) throw new Error('Failed to update roles');
      setRoleModal(null);
      await fetchUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingRoles(false);
    }
  };

  return (
    <>
      <h1 className="wp-portal__page-title">Users</h1>
      <p className="wp-portal__page-sub">Manage portal users and their roles.</p>

      {loading && <p className="wp-portal__page-sub">Loading users...</p>}

      {!loading && error && (
        <div className="wp-portal__error">{error}</div>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="wp-portal__empty">
          <p className="wp-portal__empty-label">No users found.</p>
        </div>
      )}

      {!loading && !error && users.length > 0 && (
        <div className="wp-portal__table-wrap">
          <table className="wp-portal__table">
            <thead>
              <tr>
                <th>Display Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Roles</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.display_name || '—'}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`wp-portal__badge wp-portal__badge--${user.status}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>
                    {Array.isArray(user.roles) && user.roles.length > 0
                      ? user.roles.join(', ')
                      : '—'}
                  </td>
                  <td>
                    {user.last_login_at
                      ? new Date(user.last_login_at).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="wp-portal__actions">
                    <button
                      className="wp-portal__btn wp-portal__btn--sm wp-portal__btn--ghost"
                      onClick={() => handleToggleStatus(user)}
                    >
                      {user.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="wp-portal__btn wp-portal__btn--sm"
                      onClick={() => openRoleModal(user)}
                    >
                      Roles
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {roleModal && (
        <div
          className="wp-portal__modal-backdrop"
          onClick={() => setRoleModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Manage roles"
        >
          <div
            className="wp-portal__modal"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="wp-portal__modal-title">Roles — {roleModal.name}</h2>
            <div className="wp-portal__modal-body">
              {ALL_ROLES.map(role => (
                <label key={role} className="wp-portal__modal-role-row">
                  <input
                    type="checkbox"
                    className="wp-portal__checkbox"
                    checked={roleModal.currentRoles.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>
            <div className="wp-portal__modal-actions">
              <button
                className="wp-portal__btn wp-portal__btn--ghost"
                onClick={() => setRoleModal(null)}
                disabled={savingRoles}
              >
                Cancel
              </button>
              <button
                className="wp-portal__btn wp-portal__btn--primary"
                onClick={handleSaveRoles}
                disabled={savingRoles}
              >
                {savingRoles ? 'Saving...' : 'Save Roles'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
